import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

interface RouteVisuals {
  backgroundClass: string;
  illustration?: string;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private showNavSubject = new BehaviorSubject<boolean>(false);
  private consentRequiredSubject = new BehaviorSubject<boolean>(false);
  private routeVisualsSubject = new BehaviorSubject<RouteVisuals>({
    backgroundClass: 'bg-default',
  });

  public showNav$ = this.showNavSubject.asObservable();
  public routeVisuals$ = this.routeVisualsSubject.asObservable();
  public consentRequired$ = this.consentRequiredSubject.asObservable();

  private readonly routeVisualsMap: { [key: string]: RouteVisuals } = {
    '/dashboard': {
      backgroundClass: 'bg-dashboard',
    },
    // Deprecated JWT authentication - commented out for potential future reintegration.
    // For the moment, we decided to only user Switch-Authentication
    // for the first version at least.
    // '/login': {
    //   backgroundClass: 'bg-dashboard'
    // },
    '/logs/daily': {
      backgroundClass: 'bg-daily',
    },
    '/logs/general': {
      backgroundClass: 'bg-general',
    },
  };

  constructor(private router: Router) {
    // Initialize based on current URL and update on navigation
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.updateNavVisibility(event.urlAfterRedirects);
        this.updateRouteVisuals(event.urlAfterRedirects);
        this.updateConsentVisibility(event.urlAfterRedirects);
      });

    // Set initial state
    this.updateNavVisibility(this.router.url);
    this.updateRouteVisuals(this.router.url);
    this.updateConsentVisibility(this.router.url);
  }

  private updateNavVisibility(url: string) {
    const hiddenPaths = ['/', '/login', '/register'];
    this.showNavSubject.next(!hiddenPaths.includes(url));
  }

  private updateConsentVisibility(url: string) {
    const consentNotRequiredPaths = [
      '/privacy-statements',
      '/about',
      '/faq',
      '/contact',
      '/impressum',
    ];

    this.consentRequiredSubject.next(
      !consentNotRequiredPaths.some((path) => url.includes(path))
    );
  }

  private updateRouteVisuals(url: string) {
    const matchingRoute = Object.keys(this.routeVisualsMap).find((route) =>
      url.startsWith(route)
    );

    this.routeVisualsSubject.next(
      matchingRoute
        ? this.routeVisualsMap[matchingRoute]
        : { backgroundClass: 'bg-default' }
    );
  }
}
